/**
 * AssessmentDetail — /assess/:id (VER-119)
 *
 * Detail screen for one assessment (a programme), read from GET /api/assessments/:id.
 * Shows its status and outstanding-survey counts, and routes to the real Phase 3
 * surfaces: the assessment chooser for administering a survey, and the VER-109
 * per-programme outcome report. `id` is the programme id, which is what the report
 * route keys on.
 *
 * TODO[RBAC]: gate at the route level with the AssessView permit once defined. The
 * endpoint already requires ProgrammeView, so no data is exposed in the meantime.
 */
import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Chip, Divider, Grid, Tooltip, Typography } from "@mui/material";
import {
    ArrowBackOutlined,
    AssessmentOutlined,
    GroupOutlined,
    InsightsOutlined,
    PersonOutlined,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useMobile } from "../../../hooks/useMobile";
import { VPill, VPillPositive } from "../../../core/components/VPill";
import Spinner from "../../layout/Spinner";
import { AssessmentCard, AssessmentStatus, getAssessment } from "../../../client/api/assessment";

function statusLabel(status: AssessmentStatus): string {
    switch (status) {
        case "not-started":
            return "Not started";
        case "in-progress":
            return "In progress";
        case "complete":
            return "Complete";
    }
}

function StatusPill({ status }: { status: AssessmentStatus }): JSX.Element {
    if (status === "complete") {
        return <VPillPositive size="small" label={statusLabel(status)} />;
    }
    if (status === "in-progress") {
        return <VPill size="small" label={statusLabel(status)} color="primary" />;
    }
    return <VPill size="small" label={statusLabel(status)} />;
}

function formatDate(iso: string | null): string {
    if (!iso) {
        return "—";
    }
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export const AssessmentDetail = (): JSX.Element => {
    const { isMobile, isTablet } = useMobile();
    const padding = !isTablet && isMobile ? 2 : 4;
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const [card, setCard] = useState<AssessmentCard | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const assessmentId = Number(id);
        if (!Number.isInteger(assessmentId) || assessmentId <= 0) {
            setLoading(false);
            return;
        }
        setLoading(true);
        getAssessment(assessmentId)
            .then((data) => {
                if (!cancelled) setCard(data);
            })
            .catch(() => {
                if (!cancelled) setCard(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [id]);

    if (loading) {
        return (
            <Box p={padding} width={1}>
                {!isTablet && isMobile && <Box pt={8} />}
                <Spinner />
            </Box>
        );
    }

    if (!card) {
        return (
            <Box p={padding} width={1}>
                {!isTablet && isMobile && <Box pt={8} />}
                <Alert severity="warning">
                    That assessment could not be found.{" "}
                    <Button variant="text" onClick={() => navigate("/assess")}>
                        Back to Assess hub
                    </Button>
                </Alert>
            </Box>
        );
    }

    const isGroup = card.mode === "group";
    // The chooser is the real entry point: it picks the participant, their programme and a
    // published template, then persists the response (VER-114). Linking straight to a take
    // URL would skip that and produce an unattributable run.
    const takePath = "/assess/new";

    return (
        <Box p={padding} width={1}>
            {!isTablet && isMobile && <Box pt={8} />}

            <Button
                variant="text"
                color="primary"
                startIcon={<ArrowBackOutlined />}
                onClick={() => navigate("/assess")}
                sx={{ mb: 2 }}
            >
                Back to Assess hub
            </Button>

            {/* Header */}
            <Box mb={3} display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={1}>
                    <AssessmentOutlined color="primary" sx={{ fontSize: "2rem" }} />
                    <Typography variant="h2" color="textPrimary">
                        {card.title}
                    </Typography>
                </Box>
                <StatusPill status={card.status} />
            </Box>

            <Grid container spacing={3}>
                {/* Info card */}
                <Grid item xs={12} md={7}>
                    <Card sx={{ height: "100%" }}>
                        <CardContent>
                            <Typography variant="h5" mb={2}>
                                Details
                            </Typography>
                            <Typography variant="body1" color="textSecondary" mb={2}>
                                {[
                                    card.storyCollectionName,
                                    card.ageRange,
                                    `${card.participantCount} ${
                                        card.participantCount === 1 ? "participant" : "participants"
                                    }`,
                                ]
                                    .filter(Boolean)
                                    .join(" · ")}
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                <Tooltip title={isGroup ? "Group (School)" : "1-2-1 (Social worker)"}>
                                    <Chip
                                        size="small"
                                        icon={isGroup ? <GroupOutlined /> : <PersonOutlined />}
                                        label={isGroup ? "Group" : "1-2-1"}
                                        variant="outlined"
                                    />
                                </Tooltip>
                                <Typography variant="body2" color="textSecondary">
                                    Last updated {formatDate(card.lastUpdated)}
                                </Typography>
                            </Box>

                            {card.pendingPreSurvey > 0 ? (
                                <Typography variant="body2" color="warning.dark" mt={2}>
                                    {card.pendingPreSurvey} participant(s) awaiting pre-survey.
                                </Typography>
                            ) : null}
                            {card.pendingPostSurvey > 0 ? (
                                <Typography variant="body2" color="info.dark" mt={1}>
                                    {card.pendingPostSurvey} participant(s) awaiting post-survey.
                                </Typography>
                            ) : null}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Actions card */}
                <Grid item xs={12} md={5}>
                    <Card sx={{ height: "100%" }}>
                        <CardContent>
                            <Typography variant="h5" mb={2}>
                                Actions
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={1.5}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => navigate(takePath)}
                                    disabled={card.status === "complete"}
                                >
                                    Take pre-survey
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => navigate(takePath)}
                                    disabled={card.status === "not-started"}
                                >
                                    Take post-survey
                                </Button>
                                <Button
                                    variant="text"
                                    color="primary"
                                    startIcon={<InsightsOutlined />}
                                    onClick={() => navigate(`/assess/report/${card.id}`)}
                                >
                                    View outcome report
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AssessmentDetail;
